import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Avatar,
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Start again from "Forgot password".');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/patient/reset-password`, {
        token,
        password,
        password_confirm: confirmPassword,
      });
      // Auto sign-in with the returned session token.
      loginWithToken(res.data.user);
      navigate('/');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reset password. The code may have expired — request a new one.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom color="error">
              Invalid reset link
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              No reset token was provided. Start over from "Forgot password".
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/forgot')}>Start over</Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #faf6f0 0%, #f5ebe0 100%)', p: 3,
    }}>
      <Card sx={{
        maxWidth: 460, width: '100%',
        background: 'linear-gradient(145deg, rgba(255,254,251,0.95) 0%, rgba(255,247,237,0.9) 100%)',
        border: '1px solid rgba(180, 83, 9, 0.15)',
        boxShadow: '0 25px 50px -12px rgba(180, 83, 9, 0.2)',
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
            <LockResetIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407',
          }}>
            Choose a New Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter a new password for your patient portal account.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="New Password" type="password" InputLabelProps={{ shrink: true }} value={password}
              onChange={(e) => setPassword(e.target.value)} margin="normal" required
              helperText={PASSWORD_RULES_TEXT}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <TextField fullWidth label="Confirm New Password" type="password" InputLabelProps={{ shrink: true }} value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} margin="normal" required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}
              sx={{
                mt: 3, py: 1.5, background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
                fontWeight: 700, '&:hover': { background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)' },
              }}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>

          <Button sx={{ mt: 2 }} onClick={() => navigate('/login')}>
            Back to Sign in
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
